import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { HttpService } from './http.service';
import { LanguageApiResponse } from '../interfaces/usp';

const LANGUAGE_COOKIE_KEY = 'extranet_language';
const LANGUAGE_STORAGE_KEY = 'extranet_language';
const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'it'];
const DEFAULT_LANGUAGE = 'en';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private currentLangSubject = new BehaviorSubject<string>(DEFAULT_LANGUAGE);
  currentLang$ = this.currentLangSubject.asObservable();

  public languages: string[] = SUPPORTED_LANGUAGES;
  private languagesCache$: Observable<LanguageApiResponse> | null = null;
  private cacheExpiration = 0;
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  constructor(
    public translate: TranslateService,
    private cookieService: CookieService,
    private http: HttpService
  ) {
    this.translate.addLangs(SUPPORTED_LANGUAGES);

    // Read the stored preference and seed the BehaviorSubject so anything
    // that asks for `getCurrentLanguage()` synchronously (e.g. the locale
    // HTTP interceptor) sees the right value immediately. We deliberately
    // do NOT call `translate.use()` here. The locale interceptor injects
    // this service from inside the HTTP chain, which means this constructor
    // can run while TranslateService is mid-load of fallback translations —
    // calling `translate.use(stored)` from that re-entrant position causes
    // the stored language's HTTP request to be silently dropped. The
    // initial `translate.use(...)` is now done exactly once, by the
    // APP_INITIALIZER in app.module.ts after both services are constructed.
    this.currentLangSubject.next(this.getStoredLanguage());
  }

  /**
   * Get the stored language preference.
   * Priority: 1. Cookie, 2. LocalStorage, 3. Browser language, 4. Default
   */
  private getStoredLanguage(): string {
    if (this.cookieService.check(LANGUAGE_COOKIE_KEY)) {
      const cookieLang = this.cookieService.get(LANGUAGE_COOKIE_KEY);
      if (this.isValidLanguage(cookieLang)) {
        return cookieLang;
      }
    }

    const storageLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storageLang && this.isValidLanguage(storageLang)) {
      return storageLang;
    }

    const browserLang = this.translate.getBrowserLang();
    if (browserLang && this.isValidLanguage(browserLang)) {
      return browserLang;
    }

    return DEFAULT_LANGUAGE;
  }

  private isValidLanguage(lang: string): boolean {
    return SUPPORTED_LANGUAGES.includes(lang);
  }

  /**
   * Apply language to translate service and update subject
   */
  private applyLanguage(lang: string): void {
    const validLang = this.isValidLanguage(lang) ? lang : DEFAULT_LANGUAGE;
    // Update the subject BEFORE calling translate.use(), because translate.use()
    // fires onLangChange synchronously when translations are already cached.
    // Any subscriber calling getCurrentLanguage() during onLangChange must see
    // the new locale, not the old one.
    this.currentLangSubject.next(validLang);
    this.translate.use(validLang);
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this.currentLangSubject.value;
  }

  /**
   * Set language, apply it to the UI, persist a paint-time cache in
   * cookie + localStorage, and (when authenticated) sync the choice to
   * the backend so it follows the user across devices/browsers.
   *
   * The backend `extranet_users.locale` column is the source of truth;
   * the cookie is only there so a reload doesn't flash the default
   * language while `/me` is being fetched.
   */
  public setLanguage(lang: string): void {
    const validLang = this.isValidLanguage(lang) ? lang : DEFAULT_LANGUAGE;

    this.applyLanguage(validLang);
    this.writeLocaleCache(validLang);
    this.persistLocaleToBackend(validLang);

    // Mark the backend-locale slot as "claimed" so a /me response that
    // still has the old value (PATCH not yet processed) can't race in
    // and revert the user's just-made choice.
    this.hasAppliedBackendLocale = true;
  }

  /**
   * Best-effort sync of the chosen locale to the backend. Skipped on
   * unauthenticated routes (e.g. the public login page's language
   * switcher) — the cookie cache there is sufficient until the user
   * actually has an account to associate the choice with. A failure is
   * swallowed: the local UI is already updated and the next successful
   * switch will retry.
   */
  private persistLocaleToBackend(locale: string): void {
    if (!this.isAuthenticated()) {
      return;
    }
    this.http.patch('v2/extranet/me/locale', { locale })
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private isAuthenticated(): boolean {
    return !!localStorage.getItem('token') || this.cookieService.check('token');
  }

  /**
   * Apply the locale that the backend `/me` response told us is current
   * for this user. Called from the topbar after `userChanged` fires.
   *
   * - Only fires once per page load. Subsequent `/me` responses are
   *   ignored so they can't race a user clicking the language switcher
   *   (whose PATCH wouldn't yet be visible to the next `/me` poll).
   * - Updates the UI translation AND the cookie/localStorage cache so
   *   the next reload paints in the right language before `/me` returns.
   * - Does NOT call PATCH — the backend is already the source of this
   *   value; PATCHing it back would just be a no-op round trip.
   * - Pass `{ force: true }` to bypass the one-shot guard. Used when an
   *   admin edits their own profile via the extranet-users / hotel-
   *   managers form: the form already PUTs the new locale, and we want
   *   the topbar to switch immediately rather than waiting for the next
   *   reload.
   */
  private hasAppliedBackendLocale = false;
  public applyBackendLocale(
    locale: string | undefined,
    options: { force?: boolean } = {}
  ): void {
    if (this.hasAppliedBackendLocale && !options.force) {
      return;
    }
    if (!locale || !this.isValidLanguage(locale)) {
      return;
    }
    this.hasAppliedBackendLocale = true;

    if (locale === this.getCurrentLanguage()) {
      // Cache already matches — nothing to do, but mark as applied so
      // we don't re-process subsequent /me responses for this session.
      return;
    }

    this.applyLanguage(locale);
    this.writeLocaleCache(locale);
  }

  /**
   * Back-compat shim — old call sites still use this name. New code
   * should call `applyBackendLocale` directly.
   *
   * @deprecated use `applyBackendLocale`.
   */
  public initFromUserLocale(locale: string | undefined): void {
    this.applyBackendLocale(locale);
  }

  private writeLocaleCache(locale: string): void {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    this.cookieService.set(
      LANGUAGE_COOKIE_KEY,
      locale,
      expirationDate,
      '/',
      undefined,
      false,
      'Lax'
    );
    localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
  }

  /**
   * Get available languages from API with caching
   */
  getlanguages(): Observable<LanguageApiResponse> {
    const now = Date.now();

    // Check if we have cached data that's not expired
    if (this.languagesCache$ && now < this.cacheExpiration) {
      return this.languagesCache$;
    }

    // Create new request with caching
    this.languagesCache$ = this.http.get<LanguageApiResponse>(`v2/languages`).pipe(
      shareReplay(1),
      tap(() => {
        // Set cache expiration
        this.cacheExpiration = now + this.CACHE_DURATION;
      })
    );

    return this.languagesCache$;
  }

  /**
   * Clear cache and force refresh languages data
   */
  clearCache(): void {
    this.languagesCache$ = null;
    this.cacheExpiration = 0;
  }
}
